import React, { useEffect, useState } from "react";
import { FaCheck, FaExternalLinkAlt, FaFilePdf, FaStar, FaTrashAlt } from "react-icons/fa";
import { ErrorBox } from "./common";
import { errorMessage } from "../dmApi";
import { formatBytes } from "../../../utils/directUpload";

export function MediaThumb({ media, className = "" }) {
  const url = media?.Url || media?.Public_URL;
  if (!url) return null;
  if (media.Media_Kind === "video") {
    return <video className={`dmp-media-thumb ${className}`} src={url} controls preload="metadata" playsInline />;
  }
  if (media.Media_Kind === "pdf") {
    return (
      <a className={`dmp-media-thumb dmp-media-thumb--pdf ${className}`} href={url} target="_blank" rel="noreferrer">
        <FaFilePdf aria-hidden="true" />
        PDF
      </a>
    );
  }
  return <img className={`dmp-media-thumb ${className}`} src={url} alt={media.Original_Name || ""} loading="lazy" />;
}

/**
 * Grid of uploaded media. Pass `onSelect` for a picker (click selects), or
 * `onDelete` / `onSetCover` for management; `canDelete(media)` hides delete per item.
 */
function MediaGallery({
  items,
  coverUrl,
  selectedUrl,
  onSelect,
  onDelete,
  canDelete,
  onSetCover,
  renderExtra,
  emptyText = "No media yet.",
}) {
  if (!items?.length) return <div className="dmp-empty">{emptyText}</div>;
  return (
    <div className="dmp-media-grid">
      {items.map((media) => {
        const isCover = coverUrl && coverUrl === media.Url;
        const isSelected = selectedUrl && selectedUrl === media.Url;
        return (
          <figure
            key={media.Media_Id}
            className={`dmp-media-tile${isSelected ? " is-selected" : ""}${onSelect ? " is-pickable" : ""}`}
            onClick={onSelect && media.Media_Kind !== "video" ? () => onSelect(media) : undefined}
          >
            <MediaThumb media={media} />
            {isCover ? (
              <span className="dmp-media-tile__badge">
                <FaStar aria-hidden="true" /> Cover
              </span>
            ) : null}
            <figcaption className="dmp-media-tile__cap">
              <span className="dmp-media-tile__name" title={media.Original_Name}>
                {media.Original_Name || `Media #${media.Media_Id}`}
              </span>
              <span className="dmp-media-tile__meta">
                {[media.Width && media.Height ? `${media.Width}×${media.Height}` : null, formatBytes(media.Size_Bytes)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {renderExtra ? renderExtra(media) : null}
              <span className="dmp-media-tile__actions">
                {onSelect ? (
                  <button
                    type="button"
                    className="dmp-chip-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(media);
                    }}
                  >
                    <FaCheck /> {isSelected ? "Selected" : "Use"}
                  </button>
                ) : null}
                {onSetCover && !isCover ? (
                  <button type="button" className="dmp-chip-btn" title="Make cover" onClick={() => onSetCover(media)}>
                    <FaStar />
                  </button>
                ) : null}
                <a className="dmp-chip-btn dmp-chip-btn--icon" href={media.Url} target="_blank" rel="noreferrer" title="Open">
                  <FaExternalLinkAlt />
                </a>
                {onDelete && (!canDelete || canDelete(media)) ? (
                  <button
                    type="button"
                    className="dmp-chip-btn dmp-chip-btn--icon dmp-chip-btn--danger"
                    title="Delete"
                    onClick={() => onDelete(media)}
                  >
                    <FaTrashAlt />
                  </button>
                ) : null}
              </span>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}

/**
 * Loads a project's media library and lets the user pick one item (e.g. for a post's Media URL).
 * `unlinkedOnly` lists only media not attached to any content; `excludeIds` hides already-chosen items.
 */
export function MediaPicker({
  api,
  projectId,
  selectedUrl,
  onPick,
  onClose,
  unlinkedOnly = false,
  excludeIds,
  title = "Project media",
  emptyText = "No media uploaded for this project yet. Upload it from the Media Library tab.",
}) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .listMedia(projectId, unlinkedOnly ? { content_id: "none" } : undefined)
      .then((rows) => alive && setItems(rows || []))
      .catch((err) => alive && setError(errorMessage(err, "Could not load media")));
    return () => {
      alive = false;
    };
  }, [api, projectId, unlinkedOnly]);

  const visible = items && excludeIds?.length ? items.filter((m) => !excludeIds.includes(m.Media_Id)) : items;

  return (
    <div className="dmp-media-picker">
      <div className="dmp-media-picker__head">
        <strong>{title}</strong>
        {onClose ? (
          <button type="button" className="dmp-link" onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
      <ErrorBox message={error} />
      {items == null && !error ? <div className="dmp-empty">Loading…</div> : null}
      {visible ? (
        <MediaGallery items={visible} selectedUrl={selectedUrl} onSelect={(m) => onPick(m.Url, m)} emptyText={emptyText} />
      ) : null}
    </div>
  );
}

export default MediaGallery;
